import { CheckBoxOutlineBlank } from '@mui/icons-material'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import Checkbox from '@mui/material/Checkbox'
import { Autocomplete, FormHelperText, TextField } from '@mui/material'
import React from 'react'
import { AutocompleteProps } from '@mui/material/Autocomplete'
import { FormElementProps, withFormFunctionality } from '~shared/components/reactHookForm/withFormFunctionality.hoc'
import { TextFieldProps } from '@mui/material/TextField'
import { Controller, useFormContext } from 'react-hook-form'
import { getOptionsByValues } from '~utils/getOptionByValue'
import { AutocompleteOption, PossibleAutoCompleteValue } from '~shared/models/AutocompleteOption'

const icon = <CheckBoxOutlineBlank fontSize='small' style={{ padding: '0px' }} />
const checkedIcon = <CheckBoxIcon fontSize='small' style={{ padding: '0px' }} />

export type Props = Omit<AutocompleteProps<
    AutocompleteOption, // T    -> option type
    true, // Multiple          -> allow multiple selections
    false | true, // DisableClearable  -> value cannot be emptied after selection
    false // FreeSolo           -> input does not have to be a option
    >, 'renderInput' | 'onChange' | 'onBlur' | 'value' | 'defaultValue'
    >
  & FormElementProps
  & {
  options: AutocompleteOption[]
  textFieldProps?: TextFieldProps
  defaultValue?: PossibleAutoCompleteValue
}

const FormAutocompleteMultiple: React.FC<Props> = (props) => {
  const {
    label,
    options,
    required,
    textFieldProps,
    validationError,
    ...rest
  } = props

  const { control } = useFormContext()
  return (
    <Controller
      control={control}
      name={props.name}
      defaultValue={props.defaultValue}
      render={(controllerProps) => {
        return <Autocomplete
          openOnFocus
          autoHighlight
          multiple
          disableCloseOnSelect
          options={options}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, selectedOption) => selectedOption.value === option.value}
          renderOption={(renderOptionProps, option, { selected }) => (
            <li {...renderOptionProps}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option.label}
            </li>
          )}
          style={{ width: 'auto' }}
          renderInput={(autocompleteRenderInputParams) => <>
            <TextField
              {...autocompleteRenderInputParams}
              {...textFieldProps}
              variant='standard'
              error={!!validationError}
              inputProps={{
                ...autocompleteRenderInputParams?.inputProps,
                ...textFieldProps?.inputProps,
                readOnly: true,
                autoComplete: 'new-password',
              }}
            />
            <FormHelperText error={!!validationError}>{label}{required ? '*' : ''}</FormHelperText>
          </>}
          value={getOptionsByValues(controllerProps.value, options)}
          onChange={(changeEvent, selectedOptions) => {
            const onChangeValues: PossibleAutoCompleteValue[] | undefined = selectedOptions
              ? selectedOptions.map((option) => option.value)
              : undefined

            controllerProps.onChange(onChangeValues || null)
          }}
          // eslint-disable-next-line react/jsx-handler-names -- prop provided by react hook form
          onBlur={controllerProps.onBlur}
        />
      }}
    />
  )
}

export default withFormFunctionality<Props>(FormAutocompleteMultiple)
